import React, { useMemo } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { AccessDenied, AccessLoadError, AccessProvider, useAccess } from "../components/AccessContext";
import { SkeletonPage } from "../components/skeleton/Skeleton";

// Admin pages and the permission module that controls each one. "super" pages are super-admin only.
export const adminRouteModules = [
  ["/admin/dashboard", "dashboard"],
  ["/admin/users", "users"],
  ["/admin/support-tickets", "support"],
  ["/admin/subscriptions", "subscriptions"],
  ["/admin/integrations", "integrations"],
  ["/admin/ai-providers", "ai_providers"],
  ["/admin/smtp", "smtp"],
  ["/admin/website", "website"],
  ["/admin/contact-submissions", "contact_submissions"],
  ["/admin/payment-gateways", "super"],
  ["/admin/access-control", "access_control"]
];

export const moduleForPath = (pathname) => adminRouteModules.find(([path]) => pathname === path || pathname.startsWith(`${path}/`))?.[1] || null;

export const AdminAccessProvider = AccessProvider;

export const useAdminAccess = () => {
  const context = useAccess();
  return useMemo(() => {
    const { access, can } = context;
    const canViewModule = (moduleId) => moduleId === "super" ? Boolean(access?.isSuperAdmin) : can(`admin.${moduleId}.view`);
    const firstAllowedPath = adminRouteModules.find(([, moduleId]) => canViewModule(moduleId))?.[0] || null;
    return { ...context, canViewModule, firstAllowedPath };
  }, [context]);
};

// Renders the page only when the current admin's role allows it.
export const AdminRouteGuard = ({ children }) => {
  const location = useLocation();
  const { access, loading, error, reload, canViewModule, firstAllowedPath } = useAdminAccess();
  const moduleId = moduleForPath(location.pathname);

  if (loading && !access) return <SkeletonPage label="Checking your access" />;
  if (error && !access) return <AccessLoadError error={error} onRetry={reload} />;
  if (!moduleId || canViewModule(moduleId)) return children;
  if (moduleId === "dashboard" && firstAllowedPath) return <Navigate to={firstAllowedPath} replace />;
  return <AccessDenied hasOtherPages={Boolean(firstAllowedPath)} />;
};
